/**
 * Every date box on the website shows and accepts DD/MM/YYYY.
 *
 * A browser's own date box (<input type="date">) shows the date in the
 * computer's format (mm/dd/yyyy on many PCs) and can't be told otherwise. So
 * each one gets a DD/MM/YYYY text box and a 📅 button (which opens the
 * browser's calendar) next to it, and the original box is hidden but kept:
 * its value stays "YYYY-MM-DD", so the original screens and React code read
 * and set it exactly as before, and its input/change events still fire.
 *
 * Works for the original screens and React screens alike, including date
 * boxes added later (a MutationObserver watches the page). A date box with
 * data-native-date is left alone.
 */

const proto = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')!;
const done = new WeakSet<HTMLInputElement>();

/** "2026-03-05" -> "05/03/2026" */
export function isoToDMY(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/** "5/3/2026", "05-03-2026", "05.03.26", "05032026" -> "2026-03-05"; '' if empty; null if not a real date. */
export function dmyToISO(text: string): string | null {
  const s = (text || '').trim();
  if (!s) return '';
  let d: number, mo: number, y: number;
  const sep = /^(\d{1,2})\s*[/.\-\s]\s*(\d{1,2})\s*[/.\-\s]\s*(\d{2}|\d{4})$/.exec(s);
  const packed = /^(\d{2})(\d{2})(\d{4})$/.exec(s);
  const m = sep || packed;
  if (!m) return null;
  d = +m[1]; mo = +m[2]; y = +m[3];
  if (m[3].length === 2) y += 2000;
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function enhance(native: HTMLInputElement) {
  if (done.has(native) || native.hasAttribute('data-native-date')) return;
  done.add(native);

  const wrap = document.createElement('span');
  wrap.className = 'bh-dmy';
  const text = document.createElement('input');
  text.type = 'text';
  text.inputMode = 'numeric';
  text.autocomplete = 'off';
  text.placeholder = 'DD/MM/YYYY';
  text.className = ('bh-dmy-text ' + native.className).trim();
  text.setAttribute('aria-label', native.getAttribute('aria-label') || (native.id && document.querySelector(`label[for="${native.id}"]`)?.textContent) || 'Date (DD/MM/YYYY)');
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'bh-dmy-btn';
  btn.title = 'Pick a date';
  btn.setAttribute('aria-label', 'Pick a date');
  btn.textContent = '📅';
  wrap.append(text, btn);
  native.classList.add('bh-dmy-native');
  native.tabIndex = -1;
  native.setAttribute('aria-hidden', 'true');
  native.parentNode!.insertBefore(wrap, native);

  const show = () => {
    if (document.activeElement !== text) text.value = isoToDMY(proto.get!.call(native));
    text.classList.remove('bh-dmy-bad');
  };
  const mirror = () => {
    // Follow the original box: hidden / disabled / read-only, width set in its own style.
    wrap.style.display = native.style.display === 'none' || native.hidden ? 'none' : '';
    if (native.style.maxWidth) wrap.style.maxWidth = native.style.maxWidth;
    if (native.style.width) wrap.style.width = native.style.width;
    text.disabled = btn.disabled = native.disabled;
    text.readOnly = native.readOnly;
    btn.style.display = native.readOnly ? 'none' : '';
  };

  // Code that sets the value (the original screens, React) updates the text box too.
  const own = Object.getOwnPropertyDescriptor(native, 'value') || proto; // React adds its own tracker here
  Object.defineProperty(native, 'value', {
    configurable: true,
    get() { return own.get!.call(this); },
    set(v) { own.set!.call(this, v); show(); },
  });

  /** The user typed or picked a date: set the hidden box and tell the page. */
  const commit = (iso: string, final: boolean) => {
    if (proto.get!.call(native) !== iso) {
      proto.set!.call(native, iso); // the plain setter, so React sees the change
      native.dispatchEvent(new Event('input', { bubbles: true }));
      native.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (final) { text.value = isoToDMY(iso); }
  };
  const inRange = (iso: string) => (!native.min || iso >= native.min) && (!native.max || iso <= native.max);

  text.addEventListener('input', () => {
    // Typing 05032026 becomes 05/03/2026.
    if (/^\d{8}$/.test(text.value)) text.value = `${text.value.slice(0, 2)}/${text.value.slice(2, 4)}/${text.value.slice(4)}`;
    const iso = dmyToISO(text.value);
    text.classList.remove('bh-dmy-bad');
    if (iso && /\d{4}$/.test(text.value.trim()) && inRange(iso)) commit(iso, false);
    else if (iso === '') commit('', false);
  });
  const finish = () => {
    const iso = dmyToISO(text.value);
    if (iso === null || (iso && !inRange(iso))) { text.classList.add('bh-dmy-bad'); text.title = 'Please type the date as DD/MM/YYYY'; return; }
    text.title = '';
    commit(iso, true);
  };
  text.addEventListener('blur', finish);
  text.addEventListener('keydown', e => {
    if (e.key === 'Enter') finish();
    if (e.key === 'ArrowDown' && e.altKey) { e.preventDefault(); openPicker(); }
  });
  native.addEventListener('change', show);
  native.addEventListener('input', show);
  native.addEventListener('focus', () => text.focus());

  const openPicker = () => {
    try { (native as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); }
    catch { native.focus(); }
  };
  btn.addEventListener('click', openPicker);

  new MutationObserver(mirror).observe(native, { attributes: true, attributeFilter: ['style', 'hidden', 'disabled', 'readonly', 'class'] });
  mirror();
  show();
}

function scan(root: ParentNode) {
  root.querySelectorAll<HTMLInputElement>('input[type="date"]').forEach(enhance);
  // Remove helpers whose date box was taken off the page.
  document.querySelectorAll<HTMLElement>('.bh-dmy').forEach(w => {
    const n = w.nextElementSibling;
    if (!n || !n.classList.contains('bh-dmy-native')) w.remove();
  });
}

let started = false;
export function startDmyDateInputs() {
  if (started) return;
  started = true;
  scan(document);
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    queueMicrotask(() => { queued = false; if (typeof document !== 'undefined') scan(document); });
  }).observe(document.body, { childList: true, subtree: true });
}
